import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { PUBLIC_BASE } from "../lib/publicBase";
import { Copy, Share2, Banknote, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const shareLink = data?.code ? `${PUBLIC_BASE}/auth?mode=register&ref=${data.code}` : "";

  const copy = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success("Link copied");
  };

  if (!data) return <Layout><div className="parchment p-12 text-center">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <div className="label-mystic">Sacred Circle</div>
          <h1 className="font-serif text-5xl mt-2">Referrals</h1>
          <p className="text-foreground/70 mt-2 text-sm leading-relaxed max-w-2xl">
            Share your code. Earn <span className="text-secondary font-serif">500 CAUL</span> when someone signs up,
            plus <span className="text-secondary font-serif">5%</span> of everything they spend on Cauldron for the next 90 days.
          </p>
        </div>

        <div className="parchment p-8">
          <div className="label-mystic mb-2">Your Code</div>
          <div className="font-serif text-5xl text-secondary tracking-widest mb-6" data-testid="referral-code">{data.code}</div>
          <div className="flex gap-2 items-center">
            <input readOnly value={shareLink} className="flex-1 bg-input border border-secondary/20 p-3 rounded-sm text-sm" data-testid="referral-link" />
            <button onClick={copy} className="bg-primary text-primary-foreground px-5 py-3 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2" data-testid="referral-copy">
              <Copy size={14} strokeWidth={1.25} /> Copy
            </button>
            {navigator.share && (
              <button onClick={() => navigator.share({ url: shareLink, title: "Join Cauldron" })} className="border border-secondary text-secondary px-5 py-3 rounded-sm uppercase tracking-wider text-xs flex items-center gap-2" data-testid="referral-share">
                <Share2 size={14} strokeWidth={1.25} /> Share
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="parchment p-6">
            <div className="label-mystic">Total Referrals</div>
            <div className="font-serif text-5xl text-secondary mt-2" data-testid="referral-count">{data.totalReferrals}</div>
          </div>
          <div className="parchment p-6">
            <div className="label-mystic">CAUL Earned</div>
            <div className="font-serif text-5xl text-secondary mt-2" data-testid="referral-earned">{data.totalEarnedCAUL}</div>
            <Link to="/cashout" className="inline-flex items-center gap-1.5 text-xs text-secondary hover:text-secondary/80 uppercase tracking-wider mt-3" data-testid="referrals-cashout-link">
              <Banknote size={12} strokeWidth={1.5} /> Cash out to USD <ArrowRight size={10} strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        <div>
          <div className="label-mystic mb-3">Your Circle</div>
          <div className="parchment divide-y divide-secondary/15" data-testid="referral-list">
            {data.referrals.length === 0 ? (
              <div className="p-8 text-center text-foreground/50 italic">No referrals yet — share your code to begin.</div>
            ) : data.referrals.map((r) => (
              <div key={r.id} className="p-4 flex justify-between items-center" data-testid={`referral-${r.id}`}>
                <div>
                  <div className="font-serif text-lg">{r.name}</div>
                  <div className="label-mystic">Joined {new Date(r.joinedAt).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-foreground/60">Commission until</div>
                  <div className="text-secondary text-sm">{r.expiresAt ? new Date(r.expiresAt).toLocaleDateString() : "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {data.transactions.length > 0 && (
          <div>
            <div className="label-mystic mb-3">Earnings History</div>
            <div className="parchment divide-y divide-secondary/15">
              {data.transactions.map((t) => (
                <div key={t.id} className="p-4 flex justify-between items-center text-sm">
                  <span className="text-foreground/80">{t.description}</span>
                  <span className="font-serif text-secondary">+{t.amount} CAUL</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
